import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/apiConfig';
import './FormSubmissions.css';

const FormSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const initialFilters = {
    formType: '',
    action: '',
    startDate: '',
    endDate: '',
    employeeId: '',
    search: ''
  };

  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1
  });
  

  useEffect(() => {
    fetchFormSubmissions();
  }, [filters, page, pageSize]);

  const fetchFormSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append('page', page);
      queryParams.append('pageSize', pageSize);
      
      const response = await fetch(`${API_URL}/audit/form-submissions?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch form submissions');
      }
      
      const data = await response.json();
      const submissionsPayload = data.submissions || [];

      const normalized = submissionsPayload.map((submission) => ({
        ...submission,
        browserInfo: submission.browserInfo && typeof submission.browserInfo === 'object'
          ? submission.browserInfo
          : null,
        attachments: Array.isArray(submission.attachments)
          ? submission.attachments
          : []
      }));

      setSubmissions(normalized);
      if (data.pagination) {
        setPagination({
          total: data.pagination.total || 0,
          totalPages: data.pagination.totalPages || 1
        });
      } else {
        setPagination({
          total: normalized.length,
          totalPages: 1
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

const toPrettyJSON = (value) => {
  if (!value) return null;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch (err) {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  }
};

  const toLocalInputValue = (date) => {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  const applyQuickRange = (hours) => {
    const now = new Date();
    const start = new Date(now.getTime() - hours * 60 * 60 * 1000);
    setPage(1);
    setFilters({
      ...filters,
      startDate: toLocalInputValue(start),
      endDate: toLocalInputValue(now)
    });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    setPageSize(100);
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'submitted': return 'success';
      case 'signed': return 'primary';
      case 'saved': return 'warning';
      case 'updated': return 'info';
      case 'viewed': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getFormTypeBadgeColor = (formType) => {
    switch (formType) {
      case 'W4': return 'blue';
      case 'NYIT2104': return 'purple';
      case 'I9': return 'green';
      case 'OfferLetter': return 'orange';
      case 'WageRateNotice': return 'teal';
      default: return 'gray';
    }
  };

  if (loading) {
    return <div className="form-audit-loading">Loading form submissions...</div>;
  }

  if (error) {
    return <div className="form-audit-error">Error: {error}</div>;
  }

  return (
    <div className="form-submissions-container">
      <div className="form-filters">
        <select 
          value={filters.formType} 
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, formType: e.target.value});
          }}
          className="filter-select"
        >
          <option value="">All Forms</option>
          <option value="W4">W-4 (Tax Withholding)</option>
          <option value="NYIT2104">NY IT-2104</option>
          <option value="I9">I-9 (Employment Eligibility)</option>
          <option value="OfferLetter">Offer Letter</option>
          <option value="WageRateNotice">Wage Rate Notice</option>
        </select>
        
        <select 
          value={filters.action} 
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, action: e.target.value});
          }}
          className="filter-select"
        >
          <option value="">All Actions</option>
          <option value="submitted">Submitted</option>
          <option value="signed">Signed</option>
          <option value="saved">Saved</option>
          <option value="updated">Updated</option>
        </select>
        
        <input 
          type="text" 
          placeholder="Employee ID"
          value={filters.employeeId}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, employeeId: e.target.value});
          }}
          className="filter-input"
        />
        <input
          type="text"
          placeholder="Search name, email, location"
          value={filters.search}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, search: e.target.value});
          }}
          className="filter-input search-input"
        />
        
        <input 
          type="datetime-local" 
          value={filters.startDate}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, startDate: e.target.value});
          }}
          className="filter-date"
          placeholder="Start Date"
        />
        
        <input 
          type="datetime-local" 
          value={filters.endDate}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, endDate: e.target.value});
          }}
          className="filter-date"
          placeholder="End Date"
        />

        <select 
          value={pageSize}
          onChange={(e) => {
            setPage(1);
            setPageSize(Number(e.target.value));
          }}
          className="filter-select"
        >
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
          <option value="250">250 per page</option>
          <option value="500">500 per page</option>
        </select>

        <button 
          type="button"
          className="reset-filters-btn"
          onClick={resetFilters}
        >
          Clear Filters
        </button>
      </div>

      <div className="submissions-table-container">
        <table className="submissions-table">
          <thead>
            <tr>
              <th>Submitted</th>
              <th>Form</th>
              <th>Employee</th>
              <th>Action</th>
              <th>Document Status</th>
              <th>Signed</th>
              <th>Completed</th>
              <th>Updated</th>
              <th className="hide-mobile">Location</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map(submission => (
              <tr key={submission.id}>
                <td className="time-cell">
                  {formatDateTime(submission.submittedAt)}
                </td>
                <td>
                  <span className={`form-type-badge ${getFormTypeBadgeColor(submission.formType)}`}>
                    {submission.formType}
                  </span>
                  <div className="form-name">{submission.formName}</div>
                </td>
                <td>
                  <div className="employee-name">
                    {submission.employeeFirstName} {submission.employeeLastName}
                  </div>
                  <div className="employee-meta">
                    ID: {submission.employeeId}
                    {submission.employeeEmail ? ` · ${submission.employeeEmail}` : ''}
                  </div>
                </td>
                <td>
                  <span className={`action-badge ${getActionBadgeColor(submission.action)}`}>
                    {submission.action}
                  </span>
                </td>
                <td>
                  <span className="document-status">
                    {submission.documentStatus || '-'}
                  </span>
                </td>
                <td>{submission.isSigned ? formatDateTime(submission.signedAt) : '—'}</td>
                <td>{formatDateTime(submission.completedAt)}</td>
                <td>{formatDateTime(submission.updatedAt)}</td>
                <td className="hide-mobile">{submission.locationName || '—'}</td>
                <td>
                  <button 
                    onClick={() => setSelectedSubmission(submission)}
                    className="view-details-btn"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {submissions.length === 0 && (
        <div className="no-submissions">No form submissions found for the selected filters.</div>
      )}

      <div className="quick-range-row">
        <span className="quick-range-label">Quick Range:</span>
        <div className="quick-range-buttons">
          <button type="button" onClick={() => applyQuickRange(24)}>24 Hours</button>
          <button type="button" onClick={() => applyQuickRange(24 * 7)}>7 Days</button>
          <button type="button" onClick={() => applyQuickRange(24 * 30)}>30 Days</button>
          <button type="button" onClick={() => {
            setPage(1);
            setFilters({...filters, startDate: '', endDate: ''});
          }}>All Time</button>
        </div>
      </div>

      <div className="form-pagination">
        <div className="pagination-summary">
          {pagination.total > 0
            ? `Showing ${Math.min((page - 1) * pageSize + 1, pagination.total)} – ${Math.min(page * pageSize, pagination.total)} of ${pagination.total}`
            : 'No results'}
        </div>
        <div className="pagination-controls">
          <button 
            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {pagination.totalPages}</span>
          <button 
            onClick={() => setPage(prev => Math.min(prev + 1, pagination.totalPages))}
            disabled={page >= pagination.totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {selectedSubmission && (
        <div className="submission-details-modal" onClick={() => setSelectedSubmission(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Form Submission Details</h3>
            <span className="close" onClick={() => setSelectedSubmission(null)}>&times;</span>
            
            <div className="detail-section">
              <h4>Submission Overview</h4>
              <div className="detail-grid">
                <span>Form:</span>
                <span>{selectedSubmission.formName} ({selectedSubmission.formType})</span>
                <span>Action:</span>
                <span className={`detail-status ${selectedSubmission.action}`}>{selectedSubmission.action}</span>
                <span>Document Status:</span>
                <span>{selectedSubmission.documentStatus || '—'}</span>
                <span>Submitted At:</span>
                <span>{formatDateTime(selectedSubmission.submittedAt)}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>Employee Information</h4>
              <div className="detail-grid">
                <span>Name:</span>
                <span>{selectedSubmission.employeeFirstName} {selectedSubmission.employeeLastName}</span>
                <span>Employee ID:</span>
                <span>{selectedSubmission.employeeId}</span>
                <span>Email:</span>
                <span>{selectedSubmission.employeeEmail || '—'}</span>
                <span>Location:</span>
                <span>{selectedSubmission.locationName || '—'}</span>
              </div>
            </div>

            <div className="detail-section">
              <h4>Timeline</h4>
              <div className="timeline-grid">
                <div>
                  <strong>Created</strong>
                  <div>{formatDateTime(selectedSubmission.createdAt)}</div>
                </div>
                <div>
                  <strong>Updated</strong>
                  <div>{formatDateTime(selectedSubmission.updatedAt)}</div>
                </div>
                <div>
                  <strong>Completed</strong>
                  <div>{formatDateTime(selectedSubmission.completedAt)}</div>
                </div>
                <div>
                  <strong>Signed</strong>
                  <div>{selectedSubmission.isSigned ? formatDateTime(selectedSubmission.signedAt) : 'Not signed'}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Document</h4>
              <div className="detail-grid">
                <span>File Path:</span>
                <span>{selectedSubmission.filePath || '—'}</span>
                <span>Signature Method:</span>
                <span>{selectedSubmission.signatureMethod || '—'}</span>
                <span>Legal Consent:</span>
                <span>{selectedSubmission.legalConsentGiven ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {selectedSubmission.attachments && selectedSubmission.attachments.length > 0 && (
              <div className="detail-section">
                <h4>Attachments</h4>
                {selectedSubmission.attachments.map((attachment, index) => (
                  <div key={index} className="attachment-item">
                    <span className="attachment-name">{attachment.originalName || attachment.filename}</span>
                    <span className="attachment-size">({(attachment.size / 1024).toFixed(2)} KB)</span>
                  </div>
                ))}
              </div>
            )}

            {selectedSubmission.submissionMetadata && (
              <div className="detail-section">
                <h4>Metadata</h4>
                <pre className="json-display">
                  {toPrettyJSON(selectedSubmission.submissionMetadata)}
                </pre>
              </div>
            )}

            {selectedSubmission.formData && (
              <div className="detail-section">
                <h4>Form Data</h4>
                <pre className="json-display">
                  {toPrettyJSON(selectedSubmission.formData)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;
